// src/components/admin/UserDetailDrawer.jsx  
import React, { useEffect, useState, useCallback } from "react";  
import { motion, AnimatePresence } from "framer-motion";  
import {  
  X, RefreshCw, Play, Pause, Shield, Key, Activity,  
  TrendingUp, TrendingDown, AlertTriangle, Clock, Bot,  
} from "lucide-react";  
import api from "../../api";  
  
const StatusDot = ({ tone }) => {  
  const cls = {  
    green: "bg-green-400 animate-pulse",  
    amber: "bg-amber-400",  
    red:   "bg-red-400",  
    gray:  "bg-gray-500",  
  }[tone];  
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;  
};  
  
const Section = ({ title, icon: Icon, children, action }) => (  
  <div className="border border-gray-800 rounded-xl p-4 bg-black/30">  
    <div className="flex items-center justify-between mb-3">  
      <div className="flex items-center gap-2">  
        {Icon && <Icon size={14} className="text-gray-500" />}  
        <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{title}</h4>  
      </div>  
      {action}  
    </div>  
    {children}  
  </div>  
);  
  
const UserDetailDrawer = ({ userId, onClose, onChanged }) => {  
  const [data, setData] = useState(null);  
  const [loading, setLoading] = useState(false);  
  const [busyConfigId, setBusyConfigId] = useState(null);  
  const [confirmStopAll, setConfirmStopAll] = useState(false);  
  
  const fetch = useCallback(async () => {  
    if (!userId) return;  
    setLoading(true);  
    try {  
      const res = await api.get(`/admin/users/${userId}/detail?trades_limit=15`);  
      setData(res.data);  
    } catch (err) {  
      alert("Detail load failed: " + (err.response?.data?.detail || err.message));  
      onClose();  
    } finally {  
      setLoading(false);  
    }  
  }, [userId, onClose]);  
  
  useEffect(() => { fetch(); }, [fetch]);  
  
  useEffect(() => {  
    if (!userId) return;  
    const h = (e) => e.key === "Escape" && onClose();  
    document.addEventListener("keydown", h);  
    return () => document.removeEventListener("keydown", h);  
  }, [userId, onClose]);  
  
  const toggleConfig = async (cfg) => {  
    setBusyConfigId(cfg.id);  
    try {  
      const registryId = cfg.registry_id ?? cfg.id;  
      const url = cfg.is_active  
        ? `/admin/users/${userId}/assets/${registryId}/deactivate`  
        : `/admin/users/${userId}/assets/${registryId}/activate`;  
      await api.post(url);  
      await fetch();  
      onChanged?.();  
    } catch (err) {  
      alert(`${cfg.symbol}: ${err.response?.data?.detail || err.message}`);  
    } finally {  
      setBusyConfigId(null);  
    }  
  };  
  
    const stopAll = async () => {  
    try {  
        await api.post(`/admin/users/${userId}/bots/stop-all`);  
        await fetch();  
        onChanged?.();  
        setConfirmStopAll(false);  
    } catch (err) {  
        alert("Failed: " + (err.response?.data?.detail || err.message));  
    }  
    };  
  
  return (  
    <AnimatePresence>  
      {userId && (  
        <>  
          <motion.div  
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}  
            onClick={onClose}  
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"  
          />  
          <motion.aside  
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}  
            transition={{ type: "spring", damping: 28, stiffness: 240 }}  
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[520px] bg-[#0a0a0a] border-l border-gray-800 z-50 flex flex-col shadow-2xl"  
          >  
            {/* Header */}  
            <div className="flex items-center justify-between p-4 border-b border-gray-800 flex-shrink-0">  
              <div className="flex items-center gap-3 min-w-0">  
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-yellow-600 text-black flex items-center justify-center font-bold flex-shrink-0">  
                  {data?.user?.username?.charAt(0).toUpperCase() || "?"}  
                </div>  
                <div className="min-w-0">  
                  <h3 className="text-sm font-bold text-white truncate">  
                    {data?.user?.username || "Loading…"}  
                  </h3>  
                  <p className="text-[11px] text-gray-500 truncate">{data?.user?.email || ""}</p>  
                </div>  
              </div>  
              <div className="flex items-center gap-1">  
                <button onClick={fetch} disabled={loading}  
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04]">  
                  <RefreshCw size={16} className={loading ? "animate-spin" : ""} />  
                </button>  
                <button onClick={onClose}  
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04]">  
                  <X size={16} />  
                </button>  
              </div>  
            </div>  
  
            {/* Body */}  
            <div className="flex-1 overflow-y-auto p-4 space-y-4">  
              {!data ? (  
                <div className="flex items-center justify-center py-20">  
                  <RefreshCw size={20} className="animate-spin text-gray-500" />  
                </div>  
              ) : (  
                <>  
                  {/* Account */}  
                  <Section title="Account" icon={Shield}>  
                    <div className="grid grid-cols-2 gap-2 text-xs">  
                      <div className="flex items-center gap-1.5">  
                        <StatusDot tone={data.user.is_active ? "green" : "gray"} />  
                        <span className="text-gray-400">  
                          {data.user.is_active ? "Active" : "Inactive"}  
                        </span>  
                      </div>  
                      <div className="text-gray-400">  
                        {data.user.is_superuser ? "Superuser" : "Regular user"}  
                      </div>  
                      <div className="text-gray-500 col-span-2">  
                        Joined {data.user.created_at  
                          ? new Date(data.user.created_at).toLocaleDateString()  
                          : "—"}  
                      </div>  
                    </div>  
                  </Section>  
  
                  {/* Credentials */}  
                  <Section title="Credentials" icon={Key}>  
                    {data.credentials.binance.length === 0 &&  
                     data.credentials.ig.length === 0 ? (  
                      <p className="text-xs text-gray-500">None configured</p>  
                    ) : (  
                      <div className="space-y-2">  
                        {data.credentials.binance.map((c) => (  
                          <div key={`b${c.id}`} className="flex items-center justify-between p-2 bg-black/40 rounded-lg border border-gray-800">  
                            <div className="flex items-center gap-2 min-w-0">  
                              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gold/15 text-gold rounded uppercase">Binance</span>  
                              <code className="text-[11px] text-gray-400 truncate">{c.masked_key}</code>  
                              {c.testnet && (  
                                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-blue-500/15 text-blue-400 rounded uppercase">Testnet</span>  
                              )}  
                            </div>  
                          </div>  
                        ))}  
                        {data.credentials.ig.map((c) => (  
                          <div key={`i${c.id}`} className="flex items-center justify-between p-2 bg-black/40 rounded-lg border border-gray-800">  
                            <div className="flex items-center gap-2 min-w-0">  
                              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gold/15 text-gold rounded uppercase">IG</span>  
                              <span className="text-[11px] text-gray-400 truncate">  
                                {c.username} · {c.account_id}  
                              </span>  
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${  
                                c.account_type === "DEMO"  
                                  ? "bg-blue-500/15 text-blue-400"  
                                  : "bg-green-500/15 text-green-400"  
                              }`}>{c.account_type}</span>  
                            </div>  
                          </div>  
                        ))}  
                      </div>  
                    )}  
                  </Section>  
  
                  {/* Bot configs */}  
                  <Section  
                    title="Bot configurations"  
                    icon={Bot}  
                    action={  
                    data.configs.some((c) => c.is_active) && (  
                        confirmStopAll ? (  
                        <div className="flex items-center gap-1.5">  
                            <button  
                            onClick={() => setConfirmStopAll(false)}  
                            className="text-[11px] font-semibold text-gray-400 hover:text-white"  
                            >  
                            Cancel  
                            </button>  
                            <button  
                            onClick={stopAll}  
                            className="text-[11px] font-semibold text-white bg-orange-500 hover:bg-orange-400 px-2 py-1 rounded"  
                            >  
                            Confirm  
                            </button>  
                        </div>  
                        ) : (  
                        <button  
                            onClick={() => setConfirmStopAll(true)}  
                            className="text-[11px] font-semibold text-orange-400 hover:text-orange-300"  
                        >  
                            Stop all  
                        </button>  
                        )  
                    )  
                    }  
                  >  
                    {data.configs.length === 0 ? (  
                      <p className="text-xs text-gray-500">No assets configured</p>  
                    ) : (  
                      <div className="space-y-2">  
                        {data.configs.map((cfg) => {  
                          const live = cfg.live || {};  
                          const drift = cfg.is_active && !live.task_alive;  
                          return (  
                            <div key={cfg.id}  
                              className={`p-2.5 rounded-lg border ${  
                                drift ? "border-amber-500/40 bg-amber-500/[0.04]"  
                                      : "border-gray-800 bg-black/40"  
                              }`}  
                            >  
                              <div className="flex items-center justify-between mb-1.5">  
                                <div className="flex items-center gap-2 min-w-0">  
                                  <StatusDot tone={  
                                    drift ? "amber"  
                                          : (cfg.is_active && live.task_alive) ? "green"  
                                          : "gray"  
                                  } />  
                                  <span className="text-sm font-semibold text-white">{cfg.symbol}</span>  
                                  <span className="text-[10px] text-gray-500">  
                                    {cfg.asset_type}  
                                  </span>  
                                </div>  
                                <button  
                                  onClick={() => toggleConfig(cfg)}  
                                  disabled={busyConfigId === cfg.id}  
                                  className={`p-1.5 rounded-md border text-[11px] font-semibold ${  
                                    cfg.is_active  
                                      ? "border-orange-500/40 text-orange-400 hover:bg-orange-500/10"  
                                      : "border-green-500/40 text-green-400 hover:bg-green-500/10"  
                                  } disabled:opacity-40`}  
                                >  
                                  {busyConfigId === cfg.id  
                                    ? <RefreshCw size={12} className="animate-spin" />  
                                    : cfg.is_active ? <Pause size={12} /> : <Play size={12} />}  
                                </button>  
                              </div>  
                              <div className="grid grid-cols-3 gap-1.5 text-[10px] text-gray-500">  
                                <div>Risk: <span className="text-gray-300">{(cfg.risk_percentage * 100).toFixed(1)}%</span></div>  
                                <div>Conf: <span className="text-gray-300">{cfg.confidence_threshold}</span></div>  
                                <div>Status: <span className="text-gray-300">{live.status || "—"}</span></div>  
                              </div>  
                              {drift && (  
                                <div className="flex items-center gap-1.5 mt-2 text-[10px] text-amber-300">  
                                  <AlertTriangle size={11} />  
                                  <span>DB active but task not alive — restart the service</span>  
                                </div>  
                              )}  
                              {live.has_position && (  
                                <div className="mt-2 text-[10px] text-green-400 font-semibold">  
                                  ● Position open  
                                </div>  
                              )}  
                            </div>  
                          );  
                        })}  
                      </div>  
                    )}  
                  </Section>  
  
                  {/* Recent trades */}  
                  <Section title="Recent trades" icon={Activity}>  
                    {data.recent_trades.length === 0 ? (  
                      <p className="text-xs text-gray-500">No trades yet</p>  
                    ) : (  
                      <div className="space-y-1 max-h-64 overflow-y-auto">  
                        {data.recent_trades.map((t) => {  
                          const pnl = t.pnl || 0;  
                          const pos = pnl >= 0;  
                          return (  
                            <div key={t.trade_id}  
                              className="flex items-center justify-between p-2 bg-black/40 rounded-lg border border-gray-800/60"  
                            >  
                              <div className="flex items-center gap-2 min-w-0">  
                                {pos ? <TrendingUp size={12} className="text-green-400 flex-shrink-0" />  
                                     : <TrendingDown size={12} className="text-red-400 flex-shrink-0" />}  
                                <span className="text-xs font-semibold text-white">{t.symbol}</span>  
                                <span className="text-[10px] text-gray-500 uppercase">{t.side}</span>  
                              </div>  
                              <div className="text-right flex-shrink-0">  
                                <span className={`text-xs font-bold ${pos ? "text-green-400" : "text-red-400"}`}>  
                                  {pos ? "+" : "−"}${Math.abs(pnl).toFixed(2)}  
                                </span>  
                                <p className="text-[10px] text-gray-600">  
                                  {t.timestamp ? new Date(t.timestamp).toLocaleDateString() : "—"}  
                                </p>  
                              </div>  
                            </div>  
                          );  
                        })}  
                      </div>  
                    )}  
                  </Section>  
                </>  
              )}  
            </div>  
          </motion.aside>  
        </>  
      )}  
    </AnimatePresence>  
  );  
};  
  
export default UserDetailDrawer;  